import type { TvTransition } from './protocol';
import PokerCardFace, { pokerCardLabel } from './PokerCardFace';
import { CountdownRing } from './StatusHints';
import { sim9ScorecardFromTransition, type Sim9ScorecardModel } from './sim9Hud';

/** An in-round interstitial mirrored from the phone (next player, hole
 * result, scorecard). Display only; the phone advances it. `courseHint`
 * names the Sim 9 course on the stage so a scorecard can print its FT row. */
export default function TransitionOverlay({
  transition,
  courseHint,
}: {
  transition: TvTransition;
  courseHint?: { courseId?: string | null; courseName?: string | null };
}) {
  const countdownEndsAtMs = transition.countdownEndsAtMs;
  // Sim 9 scorecards (hole done, scorecard open, round done) draw as the
  // phone's Sim9Scoreboard: HOLE, FT, PAR, and SCORE rows with the birdie
  // circles and bogey squares, instead of nine loose stat tiles.
  const scorecard = sim9ScorecardFromTransition(transition, courseHint);
  const hasCountdown = typeof countdownEndsAtMs === 'number' && Number.isFinite(countdownEndsAtMs);
  return (
    <section className="transition-overlay" aria-live="polite">
      <div className={`transition-card${scorecard ? ' with-scorecard' : ''}`}>
        <header className="transition-header">
          <strong>{transition.modeTitle}</strong>
          <span>{transition.headerRight ?? ''}</span>
        </header>

        <div className="transition-copy">
          <p className="transition-kicker">{transition.kicker}</p>
          <h2>{transition.title}</h2>
          {transition.detail ? <p className="transition-detail">{transition.detail}</p> : null}
          {transition.lines?.map((line, index) => (
            <p
              className={`transition-line tone-${line.tone ?? 'neutral'}`}
              key={`${line.text}-${index}`}
            >
              {line.text}
            </p>
          ))}
        </div>

        {scorecard ? <Sim9Scorecard model={scorecard} /> : null}

        {!scorecard && transition.stats?.length ? (
          <dl className="transition-stats">
            {transition.stats.map((stat, index) => (
              <div className={`tone-${stat.tone ?? 'neutral'}`} key={`${stat.label}-${index}`}>
                <dt>{stat.label}</dt>
                <dd>{stat.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}

        {transition.players?.length ? (
          <div className="transition-players">
            {transition.players.map((player, index) => (
              <article key={`${player.name}-${index}`}>
                <div className="transition-player-heading">
                  <span>
                    <i style={{ backgroundColor: player.accent ?? '#d4a442' }} />
                    {player.name}
                  </span>
                  {player.badge ? <strong>{player.badge}</strong> : null}
                </div>
                {player.detail ? <p>{player.detail}</p> : null}
                {player.cards?.length ? (
                  <div className="transition-card-row">
                    {player.cards.map((card, cardIndex) => (
                      <PokerCardFace
                        card={card}
                        className="transition-playing-card"
                        key={`${pokerCardLabel(card)}-${cardIndex}`}
                      />
                    ))}
                  </div>
                ) : <em>NO CARDS YET</em>}
              </article>
            ))}
          </div>
        ) : null}

        {!scorecard && transition.table ? (
          <div className="transition-table-wrap">
            <table className="transition-table">
              <thead>
                <tr>
                  <th />
                  {transition.table.columns.map((column, index) => (
                    <th key={`${column}-${index}`}>{column}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {transition.table.rows.map((row, rowIndex) => (
                  <tr key={`${row.label}-${rowIndex}`}>
                    <th>
                      {row.accent ? <i style={{ backgroundColor: row.accent }} /> : null}
                      {row.label}
                    </th>
                    {row.values.map((value, valueIndex) => (
                      <td key={`${value}-${valueIndex}`}>{value}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {hasCountdown ? (
          <div className="transition-countdown">
            <CountdownRing endsAtMs={countdownEndsAtMs} label={transition.countdownLabel ?? transition.actionLabel} />
          </div>
        ) : null}

        {transition.actionLabel || transition.footer ? (
          <footer className="transition-footer">
            {transition.footer ? <span>{transition.footer}</span> : <span />}
            {transition.actionLabel && !hasCountdown ? <strong>{transition.actionLabel}</strong> : null}
          </footer>
        ) : null}
      </div>
    </section>
  );
}

function Sim9Scorecard({ model }: { model: Sim9ScorecardModel }) {
  const hasFt = model.holes.some((hole) => hole.distanceFt != null);
  const totalFt = model.holes.reduce((sum, hole) => sum + (hole.distanceFt ?? 0), 0);
  const hasPar = model.holes.some((hole) => hole.par != null);
  return (
    <div className="sim9-card-wrap">
      <table className="sim9-card" aria-label="Scorecard">
        <thead>
          <tr>
            <th scope="col">HOLE</th>
            {model.holes.map((hole) => <th scope="col" key={hole.number}>{hole.number}</th>)}
            <th scope="col">TOT</th>
          </tr>
        </thead>
        <tbody>
          {hasFt ? (
            <tr className="sim9-card-ft">
              <th scope="row">FT</th>
              {model.holes.map((hole) => <td key={hole.number}>{hole.distanceFt ?? ''}</td>)}
              <td>{totalFt || ''}</td>
            </tr>
          ) : null}
          {hasPar ? (
            <tr className="sim9-card-par">
              <th scope="row">PAR</th>
              {model.holes.map((hole) => <td key={hole.number}>{hole.par ?? ''}</td>)}
              <td>{model.parTotal ?? ''}</td>
            </tr>
          ) : null}
          {model.rows.map((row, rowIndex) => (
            <tr className="sim9-card-score" key={`${row.label}-${rowIndex}`}>
              <th scope="row">
                {row.accent ? <i style={{ backgroundColor: row.accent }} aria-hidden="true" /> : null}
                {row.label}
              </th>
              {row.cells.map((cell, index) => (
                <td key={index}>
                  <span className={`sim9-mark${cell.mark ? ` mark-${cell.mark}` : ' mark-empty'}`}>{cell.text}</span>
                </td>
              ))}
              <td className="sim9-card-total">
                {row.total}
                {row.toPar ? <small>{row.toPar}</small> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
